/**
 * Equality and "is-answered" helpers for CMM questionnaire answers.
 *
 * Questionnaire answers can take several shapes — `null`, `undefined`,
 * a (possibly blank) string, or an array of strings. These helpers
 * normalize that variation behind a single notion of "answered" and
 * "equivalent" so call sites don't reinvent the rules each time.
 *
 * @module pharmaQuestionaire/utils/answerEquality
 */
import { Answer } from "../../../data-model/cmmMedicalNeccessityQuestionnaire";

/**
 * Returns true when `value` carries a usable answer, i.e. it is not `null`,
 * not `undefined`, not an empty string after trimming, and not an empty
 * array. Used to enforce a single notion of "answered" across the
 * heterogeneous {@link Answer} shapes.
 *
 * @param value - Answer-shaped value (or undefined)
 * @returns `true` when the value would count as a meaningful answer
 */
export function hasMeaningfulValue(
  value: Answer | string | undefined,
): boolean {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return value.trim() !== "";
}

/**
 * Returns true when two answers are equivalent.
 *
 * - Strings compare directly.
 * - Arrays compare order-insensitively (treated as sets).
 * - Mixed shapes (string vs array) are unequal.
 * - `null` / `undefined` / empty string / empty array are all "no answer"
 *   and equivalent to each other.
 *
 * @param a - First answer
 * @param b - Second answer
 * @returns `true` when both represent the same logical answer
 */
export function areAnswersEqual(
  a: Answer | string | undefined,
  b: Answer | string | undefined,
): boolean {
  const aEmpty = !hasMeaningfulValue(a);
  const bEmpty = !hasMeaningfulValue(b);
  if (aEmpty && bEmpty) return true;
  if (aEmpty !== bEmpty) return false;
  const aArr = Array.isArray(a);
  const bArr = Array.isArray(b);
  if (aArr !== bArr) return false;
  if (aArr && bArr) {
    const aSorted = [...(a as string[])].sort();
    const bSorted = [...(b as string[])].sort();
    return (
      aSorted.length === bSorted.length &&
      aSorted.every((v, i) => v === bSorted[i])
    );
  }
  return a === b;
}
